import { useState } from 'react'

import { useApolloClient } from '@apollo/client'
import _ from 'lodash'
import { ItList } from 'types/graphql'

import { navigate, routes } from '@redwoodjs/router'

import { BuyEvent } from 'src/components/features/ItListDetails'
import { toast } from 'src/components/widgets/Toaster'
import { LOCAL_STORAGE_AFFILIATED_ITLIST_KEY } from 'src/lib/constants'
import { useAnalytics } from 'src/lib/hooks/analytics/useAnalytics'
import { get } from 'src/lib/storedAffiliations'
import {
  buildQueryParamsWithItList,
  getAbsoluteUrl,
  slugify,
} from 'src/lib/utils'

import { useCurrentUser } from '../auth/useCurrentUser'
import { useSetBackUrl } from '../useSetBackUrl'

export type GetCheckoutSessionSecretResponse = {
  checkoutSessionSecret: {
    clientSecret: string
    purchaseDate: string
  }
}

export type GetCheckoutSessionSecretRequest = {
  itListId: number
  returnUrl: string
  affiliateId?: number
  promoCode?: string
}

export const GET_CHECKOUT_SESSION_SECRET = gql`
  query GetCheckoutSessionSecret(
    $itListId: Int!
    $returnUrl: String!
    $affiliateId: Int
    $promoCode: String
  ) {
    checkoutSessionSecret(
      itListId: $itListId
      returnUrl: $returnUrl
      affiliateId: $affiliateId
      promoCode: $promoCode
    ) {
      clientSecret
      purchaseDate
    }
  }
`

type PurchaseItListProps = {
  itList: ItList
  promoCode?: string
  forceCheckout?: boolean
  // in case of login during checkout, we want to redirect
  // to the ItList https://itlist.atlassian.net/browse/WEB-1419
  redirectIfOwned?: boolean
}

type InitPurchaseItListProps = Omit<PurchaseItListProps, 'isFree'> & {
  eventName: BuyEvent
}

export default function usePurchaseItList() {
  const { isAuthenticated } = useCurrentUser()
  const client = useApolloClient()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const { track } = useAnalytics()
  const setBackUrl = useSetBackUrl()

  const initPurchase = ({ eventName, ...props }: InitPurchaseItListProps) => {
    const itList = props.itList
    const creator = props.itList.creator
    if (!creator) {
      throw new Error('an ItList object is missing the creator properties')
    }
    setBackUrl()
    const baseTrackingProps = {
      owningCreatorEmail: creator.email,
      owningCreatorId: `${creator.id}`,
      owningCreatorUsername: creator.username,
      productId: `${itList.id}`,
    }
    if (eventName === 'Buy Now Clicked') {
      track({
        eventName: 'Buy Now Clicked',
        props: {
          ...baseTrackingProps,
          quantity: 1,
          currency: 'USD',
          sku: `${itList.id}`,
          price: itList.price,
          name: itList.name,
        },
      })
    } else {
      track({
        eventName: 'Affiliate Access Granted',
        props: baseTrackingProps,
      })
    }
    return continuePurchase(props)
  }

  const continuePurchase = async ({
    itList,
    promoCode,
    forceCheckout,
    redirectIfOwned,
  }: PurchaseItListProps): Promise<string | null> => {
    if (isAuthenticated) {
      setLoading(true)
      try {
        const { data, error } = await client.query<
          GetCheckoutSessionSecretResponse,
          GetCheckoutSessionSecretRequest
        >({
          query: GET_CHECKOUT_SESSION_SECRET,
          variables: {
            promoCode,
            itListId: itList.id,
            returnUrl: getAbsoluteUrl(
              routes.checkoutCompleted({
                ...buildQueryParamsWithItList(itList), // https://stripe.com/docs/payments/checkout/custom-redirect-behavior#return-url
                sessionId: '{CHECKOUT_SESSION_ID}',
              })
            ),
            affiliateId: get(
              `${LOCAL_STORAGE_AFFILIATED_ITLIST_KEY}:${itList.id}`
            ),
          },
        })
        const clientSecret = data?.checkoutSessionSecret?.clientSecret
        const purchaseDate = data?.checkoutSessionSecret?.purchaseDate
        const redirectToOwnedItList = purchaseDate && redirectIfOwned
        setLoading(false)
        if (error) {
          setError(error.message)
          toast(
            'error',
            "Sorry, this ItList is unavailable at the moment, we'll let the creator know about this."
          )
        } else if (clientSecret && !redirectToOwnedItList) {
          // redirect to stripe checkout
          navigate(
            routes.checkoutForm(
              buildQueryParamsWithItList(
                { ...itList, purchaseDate },
                {
                  session: clientSecret,
                }
              )
            ),
            { replace: true }
          )
        } else {
          // itList is free
          goToUnlockedItList(itList)
        }
      } catch (err) {
        setError(err.message)
        setLoading(false)
        toast(
          'error',
          "Sorry, this ItList is unavailable at the moment, we'll let the creator know about this."
        )
      }
    } else if (itList.price === 0 && !forceCheckout) {
      // show unlocked free itList without the need to authenticate
      goToUnlockedItList(itList)
    } else {
      // authenticate before buying/unlocking an itList
      navigate(
        routes.checkoutRegistration(buildQueryParamsWithItList(itList)),
        {
          replace: true,
        }
      )
    }
    return null
  }

  const goToUnlockedItList = (itList: ItList) => {
    if (itList.relationship === 'affiliate') {
      // need to reload for the first access from an affiliate
      // because the itList requested by the cell is still obfuscated
      // subsequent access will have hasPurchasedItList === true
      window.location.reload()
    } else {
      navigate(
        routes.itList({
          id: itList.id,
          title: slugify(itList.name),
          unlocked: true,
        }),
        { replace: true }
      )
    }
  }

  return {
    initPurchase,
    continuePurchase,
    loading,
    error,
  }
}
